import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { api } from "@/lib/api";
import {
  LayoutGrid, CheckSquare, Layers, Briefcase, Flame, Shield, CalendarRange, Clock, Target, Timer, UserCircle,
  FileCheck, MessageCircle, Receipt, Copy, ShoppingBag, Search, ShieldAlert,
  Sparkles, BarChart3, Users, CreditCard, LogOut, Eye, Network,
  ChevronDown, Columns3, AlertTriangle, CalendarClock, Repeat,
  GitBranch, GanttChart, BookOpen, FileBarChart, Plug, Menu, X, MailWarning, Sun, Moon, Activity, Megaphone,
  CalendarDays, FolderOpen, RotateCcw, Mail, Truck, Radio, DollarSign, FileText, Brain, Tag, Plane, SlidersHorizontal, Zap, Users2, LifeBuoy, Headset, PhoneCall, HeartPulse,
  Grid2x2, Package, Bookmark, FileSignature, Landmark, FileDown, Bot, Building2, Clock3, ShieldCheck, Boxes,
  Sunrise, GitPullRequest, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/app/Brand";
import NotificationBell from "@/components/app/NotificationBell";
import TimerWidget from "@/components/app/TimerWidget";
import PresencePill from "@/components/app/PresencePill";
import CommandPalette from "@/components/app/CommandPalette";
import OnboardingWizard from "@/components/app/OnboardingWizard";
import { toast } from "sonner";

const ACTIVE_PROJECT_LS_KEY = "cw_active_project_id";

/** Read the active project from the URL (if user is in a project route) or
 *  from localStorage (last-visited). Returns null when no context exists yet.
 *
 *  SECURITY NOTE: This stores ONLY a project_id string (a non-sensitive UI
 *  hint for "remember which project I last looked at"). Authentication tokens
 *  are NEVER stored here — they live in httpOnly cookies set by the backend
 *  on /api/auth/login. Static analysers sometimes flag this as "localStorage
 *  for sensitive data" — they're false-positive matching on the storage call.
 */
const useActiveProjectId = () => {
  const { pathname } = useLocation();
  return useMemo(() => {
    const m = pathname.match(/^\/workspace\/([^/]+)/);
    if (m && m[1] !== "") {
      try { localStorage.setItem(ACTIVE_PROJECT_LS_KEY, m[1]); } catch { /* SSR / quota */ }
      return m[1];
    }
    try { return localStorage.getItem(ACTIVE_PROJECT_LS_KEY); } catch { return null; }
  }, [pathname]);
};

/** Single nav row used by AppShell. Centralises hover/active styling so the
 *  collapsible Workspace group and the flat groups share identical chrome. */
const SidebarNavLink = ({ item, isProjectChild = false, disabledHint = false }) => {
  const { to, label, icon: Icon, testid, badge, badgeColor, isPremium, onClick } = item;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={testid}
      end={isProjectChild ? false : undefined}
      className={({ isActive }) =>
        `w-full flex items-center justify-between px-3 py-2 rounded-xl transition-colors duration-100 group text-left ${
          isActive
            ? "bg-violet-50 text-violet-700 font-medium"
            : `text-gray-500 hover:text-gray-800 hover:bg-gray-100 font-normal ${
                disabledHint ? "opacity-50" : ""
              }`
        }`
      }
    >
      {({ isActive }) => (
        <>
          <div className="flex items-center space-x-2.5 text-sm min-w-0">
            <Icon
              className={`shrink-0 transition-colors ${isProjectChild ? "w-3.5 h-3.5" : "w-4 h-4"} ${
                isActive ? "text-violet-600" : "text-gray-400 group-hover:text-gray-600"
              }`}
              strokeWidth={1.75}
            />
            <span className="truncate">{label}</span>
            {isPremium && (
              <span className="text-[8px] font-bold bg-gradient-to-r from-violet-500 to-indigo-500 text-white px-1.5 py-0.5 rounded-full tracking-wide">
                AI
              </span>
            )}
          </div>
          {badge ? (
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${badgeColor || "bg-gray-100 text-gray-500 border-gray-200"}`}
              data-testid={`${testid}-badge`}
            >
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
};


// ── User dropdown menu (Scoro-style) ─────────────────────────────────────────

const UserMenu = ({ user, initials, planLabel, navigate, handleLogout, toggleTheme, theme }) => {
  const [open, setOpen] = useState(false);
  const isManager = user?.role === "admin" || user?.role === "owner";

  const menuItems = [
    {
      group: "My Account",
      items: [
        { icon: UserCircle, label: "Profile settings", onClick: () => { navigate("/account"); setOpen(false); } },
        { icon: CreditCard, label: "Plan & billing", onClick: () => { navigate("/billing"); setOpen(false); } },
        { icon: Users, label: "Manage users", onClick: () => { navigate("/team"); setOpen(false); } },
      ],
    },
    {
      group: "Admin & Settings",
      items: [
        { icon: Plug, label: "Integrations", onClick: () => { navigate("/integrations"); setOpen(false); } },
        { icon: Boxes, label: "Custom Modules", onClick: () => { navigate("/custom-modules"); setOpen(false); } },
        ...(isManager ? [
          { icon: SlidersHorizontal, label: "Custom Fields", onClick: () => { navigate("/custom-fields"); setOpen(false); } },
          { icon: FileDown, label: "PDF Customization", onClick: () => { navigate("/pdf-settings"); setOpen(false); } },
          { icon: Building2, label: "Business Entities", onClick: () => { navigate("/entities"); setOpen(false); } },
          { icon: Zap, label: "Automations", onClick: () => { navigate("/automations"); setOpen(false); } },
          { icon: Shield, label: "Single Sign-On", onClick: () => { navigate("/sso-settings"); setOpen(false); } },
          { icon: Users2, label: "SCIM Provisioning", onClick: () => { navigate("/scim-settings"); setOpen(false); } },
        ] : []),
        { icon: ShoppingBag, label: "Marketplace", onClick: () => { navigate("/marketplace"); setOpen(false); } },
      ],
    },
    {
      group: "Options",
      items: [
        { icon: theme === "dark" ? Sun : Moon, label: theme === "dark" ? "Switch to light" : "Switch to dark", onClick: () => { toggleTheme(); setOpen(false); } },
        { icon: LogOut, label: "Log out", onClick: () => { handleLogout(); setOpen(false); }, danger: true },
      ],
    },
  ];

  return (
    <div className="p-3 border-t border-gray-100 relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
        data-testid="sidebar-user-account-link"
      >
        <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center text-white font-bold text-xs overflow-hidden shrink-0 shadow-sm shadow-violet-200">
          {user?.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover rounded-xl" /> : initials}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-xs font-semibold text-gray-800 truncate" data-testid="user-name">{user?.name || user?.email}</div>
          <div className="text-[10px] text-gray-400 truncate capitalize">{planLabel} plan</div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-3 right-3 bottom-full mb-2 z-50 bg-white border border-gray-100 rounded-2xl shadow-xl shadow-gray-200/60 overflow-hidden">
            {/* User header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50">
              <div className="w-9 h-9 rounded-xl bg-violet-600 grid place-items-center text-white font-bold text-sm overflow-hidden shrink-0">
                {user?.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover rounded-xl" /> : initials}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{user?.name || user?.email}</div>
                <div className="text-xs text-gray-400 truncate">{user?.email}</div>
              </div>
            </div>

            {/* Menu groups */}
            {menuItems.map(({ group, items }) => (
              <div key={group} className="py-1.5">
                <div className="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{group}</div>
                {items.map(({ icon: Icon, label, onClick, danger }) => (
                  <button
                    key={label}
                    onClick={onClick}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                      danger
                        ? "text-red-500 hover:bg-red-50"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${danger ? "text-red-400" : "text-gray-400"}`} strokeWidth={1.75} />
                    {label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Refactored AppShell — dark slate-950 chrome to match the Command Centre.
 * Nav grouped by operational business unit. Badges are wired to real backend
 * counts (open deals, overdue invoices, pending compliance docs) and refresh
 * every 60s so the operator always sees the truth.
 */
const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const activeProjectId = useActiveProjectId();
  const [counts, setCounts] = useState({ deals_open: 0, invoices_overdue: 0, compliance_pending: 0 });
  // Every group is collapsible; a group with no entry here is treated as
  // expanded (see `isExpanded` below) so the sidebar looks unchanged on
  // first load — collapsing anything is an explicit, remembered action.
  const [expandedGroups, setExpandedGroups] = useState({});
  // Mobile drawer state — desktop ignores this entirely (sidebar is sticky there).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { pathname } = useLocation();

  // Auto-close the mobile drawer on route change so tapping a nav item dismisses it.
  useEffect(() => { setMobileNavOpen(false); }, [pathname]);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  /** Resolve a workspace sub-item link. If no active project context exists yet,
   *  send the user to the workspace picker with a toast hint. */
  const resolveProjectRoute = useCallback((suffix) => {
    if (activeProjectId) return `/workspace/${activeProjectId}${suffix}`;
    return null;
  }, [activeProjectId]);

  const onProjectChildClick = useCallback((label) => (e) => {
    if (activeProjectId) return; // real link will navigate
    e.preventDefault();
    toast.info(`Pick a project from Workspace to open ${label}.`);
    navigate("/workspace");
  }, [activeProjectId, navigate]);

  const fetchCounts = useCallback(async () => {
    try {
      const [d, inv, comp] = await Promise.all([
        api.get("/deals").catch(() => ({ data: [] })),
        api.get("/invoices/aging").catch(() => ({ data: { invoice_breakdown: [] } })),
        api.get("/compliance/documents?status=pending_review").catch(() => ({ data: [] })),
      ]);
      setCounts({
        deals_open: (d.data || []).filter((x) => !["won", "lost"].includes(x.stage)).length,
        invoices_overdue: (inv.data?.invoice_breakdown || []).filter((i) => i.days_overdue > 0).length,
        compliance_pending: (comp.data || []).length,
      });
    } catch {
      // silent — chrome badge is decorative
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchCounts();
    // Stretch the badge poll to 2 minutes and skip when tab hidden to reduce
    // backend load in resource-constrained production deployments.
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchCounts();
    };
    const id = setInterval(tick, 120000);
    return () => clearInterval(id);
  }, [user, fetchCounts]);

  // Unified navigation matrix grouped by operational business unit.
  // The "workspace" group is collapsible — clicking the header expands/collapses
  // the 8 project sub-views. Sub-views inherit the last-active project context.
  const navigationGroups = [
    {
      groupName: "Overview",
      items: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
        { to: "/dashboards", label: "My Dashboards", icon: Columns3, testid: "nav-dashboards" },
        { to: "/my-tasks", label: "My Tasks", icon: CheckSquare, testid: "nav-my-tasks" },
        { to: "/task-matrix", label: "Task Matrix", icon: Grid2x2, testid: "nav-task-matrix" },
        { to: "/planner", label: "Planner", icon: CalendarRange, testid: "nav-planner" },
        { to: "/saved-views", label: "Saved Views", icon: Bookmark, testid: "nav-saved-views" },
        { to: "/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
        { to: "/help-center", label: "Help Center", icon: LifeBuoy, testid: "nav-help-center" },
        { to: "/live-support", label: "Live Support", icon: PhoneCall, testid: "nav-live-support" },
        {
          // Top-level Chat — uses the last-active project context via resolveProjectRoute.
          to: resolveProjectRoute("/chat") || "/workspace",
          label: "Chat",
          icon: MessageCircle,
          testid: "nav-chat",
          onClick: onProjectChildClick("Chat"),
        },
      ],
    },
    {
      groupName: "Workspace",
      collapsibleKey: "workspace",
      // The parent row itself is a real link to the project picker / workspace list.
      parent: {
        to: "/workspace",
        label: "Workspace",
        icon: Layers,
        testid: "nav-workspace",
      },
      items: [
        { suffix: "/board", label: "Board", icon: Columns3, testid: "nav-board" },
        { suffix: "/raid", label: "RAID", icon: AlertTriangle, testid: "nav-raid" },
        { suffix: "/standup", label: "Daily Standup", icon: Sunrise, testid: "nav-standup" },
        { suffix: "/meetings", label: "Meetings", icon: CalendarClock, testid: "nav-meetings" },
        { suffix: "/sprints", label: "Sprints", icon: Repeat, testid: "nav-sprints" },
        { suffix: "/dependencies", label: "Dependencies", icon: GitBranch, testid: "nav-dependencies" },
        { suffix: "/gantt", label: "Gantt", icon: GanttChart, testid: "nav-gantt" },
        { suffix: "/change-requests", label: "Change Requests", icon: GitPullRequest, testid: "nav-change-requests" },
        { suffix: "/wiki", label: "Wiki", icon: BookOpen, testid: "nav-wiki" },
        { suffix: "/report", label: "Report", icon: FileBarChart, testid: "nav-report" },
      ],
    },
    {
      groupName: "Workflow",
      items: [
        { to: "/quotes", label: "Quotes", icon: FileSignature, testid: "nav-quotes" },
        { to: "/activity-types", label: "Activity Types", icon: Clock3, testid: "nav-activity-types" },
        { to: "/sow", label: "SOW Pipeline", icon: FileText, testid: "nav-sow" },
      ],
    },
    {
      groupName: "Sales & Financials",
      items: [
        {
          to: "/deals", label: "Deals (CRM)", icon: Briefcase, testid: "nav-deals",
          badge: counts.deals_open || null,
        },
        {
          to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices",
          badge: counts.invoices_overdue || null,
          badgeColor: "bg-amber-500/10 text-amber-400 border-amber-500/20",
        },
        { to: "/purchase-orders", label: "Purchase Orders", icon: Truck, testid: "nav-purchase-orders" },
        { to: "/bills", label: "Bills", icon: FileText, testid: "nav-bills" },
        { to: "/retainers", label: "Retainers", icon: DollarSign, testid: "nav-retainers" },
        { to: "/catalog", label: "Catalogue", icon: Tag, testid: "nav-catalog" },
        { to: "/company-budgets", label: "Company Budgets", icon: Landmark, testid: "nav-company-budgets" },
        { to: "/client-comms", label: "Client Comms", icon: Mail, testid: "nav-client-comms" },
      ],
    },
    {
      groupName: "Delivery Engine",
      items: [
        { to: "/goals", label: "Goals & OKRs", icon: Target, testid: "nav-goals" },
        { to: "/portfolio", label: "Portfolio", icon: Network, testid: "nav-portfolio" },
        { to: "/pipeline", label: "Pipeline", icon: GanttChart, testid: "nav-pipeline" },
        { to: "/burndown", label: "Burndown", icon: Flame, testid: "nav-burndown" },
        { to: "/templates", label: "Templates", icon: Copy, testid: "nav-templates" },
        { to: "/task-bundles", label: "Task Bundles", icon: Package, testid: "nav-task-bundles" },
        { to: "/resource-calendar", label: "Resource Calendar", icon: CalendarDays, testid: "nav-resource-calendar" },
        { to: "/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents" },
      ],
    },
    {
      groupName: "Governance & Quality",
      items: [
        { to: "/governance", label: "Governance", icon: Shield, testid: "nav-governance" },
        {
          to: "/compliance", label: "Compliance", icon: FileCheck, testid: "nav-compliance",
          badge: counts.compliance_pending || null,
          badgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/20",
        },
        { to: "/contacts", label: "Comms Charter", icon: MessageCircle, testid: "nav-contacts" },
        { to: "/retros", label: "Retrospectives", icon: RotateCcw, testid: "nav-retros" },
        { to: "/status-pages", label: "Status Pages", icon: Radio, testid: "nav-status-pages" },
      ],
    },
    {
      groupName: "People & Time",
      items: [
        { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
        { to: "/permissions", label: "Permissions", icon: ShieldCheck, testid: "nav-permissions" },
        { to: "/capacity", label: "Capacity", icon: CalendarRange, testid: "nav-capacity" },
        { to: "/timesheets", label: "Timesheets", icon: Clock, testid: "nav-timesheets" },
        { to: "/time-tracking", label: "Time Tracking", icon: Timer, testid: "nav-time-tracking" },
        { to: "/time-off", label: "Time Off", icon: Plane, testid: "nav-time-off" },
      ],
    },
    {
      groupName: "Insights & AI",
      items: [
        { to: "/reporting", label: "Reporting", icon: BarChart3, testid: "nav-reporting" },
        { to: "/health-score", label: "Health Score", icon: HeartPulse, testid: "nav-health-score" },
        { to: "/intelligence", label: "Predictions", icon: Brain, testid: "nav-intelligence" },
        { to: "/assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-assistant", isPremium: true },
        { to: "/mcp-settings", label: "MCP Server", icon: Bot, testid: "nav-mcp-settings" },
      ],
    },
    {
      groupName: "Platform Admin",
      items: [
        ...(user?.role === "superadmin" ? [
          { to: "/admin", label: "Admin Panel", icon: ShieldAlert, testid: "nav-admin" },
          { to: "/support-queue", label: "Support Queue", icon: Headset, testid: "nav-support-queue" },
        ] : []),
        ...(user?.role === "superadmin" || user?.role === "marketing_admin" ? [
          { to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
        ] : []),
      ],
    },
  ].filter((group) => group.parent || group.items.length > 0);

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();
  const planLabel = (user?.plan || "free").replace(/_/g, " ");

  return (
    <div className="min-h-screen md:grid md:grid-cols-[288px_1fr]">
      {/* Mobile hamburger — visible only on <md. Stays above the page content. */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 w-10 h-10 grid place-items-center bg-white text-gray-600 rounded-xl shadow-md border border-gray-100"
        aria-label="Open navigation"
        data-testid="mobile-nav-open-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay (under the drawer) */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}

      <aside
        className={`bg-white border-r border-gray-100 flex flex-col font-sans select-none shadow-sm
          md:sticky md:top-0 md:h-screen
          fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] transform transition-transform duration-200
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        data-testid="app-sidebar"
      >
        {/* Brand header */}
        <Link to="/" className="h-14 flex items-center justify-between px-5 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid="app-brand">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">
              Combined<span className="text-violet-600">Work</span>
            </span>
          </div>
          {/* Close button — mobile only */}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setMobileNavOpen(false); }}
            className="md:hidden text-gray-400 hover:text-gray-700 p-1 transition-colors"
            aria-label="Close navigation"
            data-testid="mobile-nav-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </Link>

        {/* Navigation scroll yard */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navigationGroups.map((group) => {
            const groupKey = group.collapsibleKey || group.groupName.toLowerCase().replace(/[^a-z]+/g, "-");
            // Undefined means "never toggled" — treated as expanded so the
            // sidebar's default look is unchanged; only an explicit `false`
            // collapses a group.
            const isExpanded = expandedGroups[groupKey] !== false;
            return (
              <div key={group.groupName} className="space-y-1.5">
                {/* Group header. Groups with a `parent` (Workspace) fold the
                    toggle into the parent's own row instead of a separate
                    uppercase label above it — one "Workspace" row, not two. */}
                {group.parent ? (
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => setExpandedGroups((m) => ({ ...m, [groupKey]: !isExpanded }))}
                      className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      data-testid={`nav-group-${groupKey}-toggle`}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.groupName}`}
                    >
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`}
                        strokeWidth={2.5}
                      />
                    </button>
                    <div className="flex-1 min-w-0">
                      <SidebarNavLink item={group.parent} />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setExpandedGroups((m) => ({ ...m, [groupKey]: !isExpanded }))}
                    className="w-full px-3 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid={`nav-group-${groupKey}-toggle`}
                    aria-expanded={isExpanded}
                  >
                    <span>{group.groupName}</span>
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`}
                      strokeWidth={2.5}
                    />
                  </button>
                )}

                {/* Group items */}
                {isExpanded && (
                  <div className={`space-y-0.5 ${group.parent ? "pl-4 border-l border-gray-100 ml-3" : ""}`}>
                    {group.items.map((item) => {
                      // Workspace sub-items use `suffix` and inherit the active-project context.
                      if (item.suffix !== undefined) {
                        return (
                          <SidebarNavLink
                            key={item.testid}
                            item={{
                              ...item,
                              to: resolveProjectRoute(item.suffix) || "/workspace",
                              onClick: onProjectChildClick(item.label),
                            }}
                            isProjectChild
                            disabledHint={!activeProjectId}
                          />
                        );
                      }
                      return <SidebarNavLink key={item.to} item={item} />;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tenant context footer */}
        <UserMenu
          user={user}
          initials={initials}
          planLabel={planLabel}
          navigate={navigate}
          handleLogout={handleLogout}
          toggleTheme={toggleTheme}
          theme={theme}
        />
      </aside>

      <main className="min-h-screen overflow-x-hidden relative bg-gray-50 text-gray-900 pt-14">
        {/* Global notification bell + presence pill — fixed top-right, above page content. Sized down on mobile. */}
        <div className="fixed top-3 right-3 md:right-4 z-40 flex items-stretch gap-1.5">
          <div className="bg-white/95 backdrop-blur border border-gray-100 rounded-xl shadow-sm flex items-center px-2">
            <TimerWidget />
          </div>
          <div className="bg-white/95 backdrop-blur border border-gray-100 rounded-xl shadow-sm flex items-center">
            <PresencePill />
          </div>
          <div className="bg-white/95 backdrop-blur border border-gray-100 rounded-xl shadow-sm">
            <NotificationBell />
          </div>
          <button
            onClick={() => setPaletteOpen(true)}
            className="bg-white/95 backdrop-blur border border-gray-100 rounded-xl shadow-sm px-3 py-2 flex items-center gap-2 text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="open-search-btn"
            title="Search (⌘K)"
          >
            <Search className="w-4 h-4" />
            <kbd className="hidden lg:block text-[10px] font-mono text-gray-400 border border-gray-200 px-1.5 py-0.5 rounded-md">⌘K</kbd>
          </button>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <OnboardingWizard />
        {user?.role === "viewer" && (
          <div
            className="bg-amber-50 border-b border-amber-100 text-amber-700 px-8 lg:px-12 py-2 flex items-center gap-2 text-xs"
            data-testid="viewer-readonly-banner"
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={1.7} />
            <span className="uppercase tracking-wide font-medium">Read-only access</span>
            <span>— ask an admin to upgrade your role to make changes.</span>
          </div>
        )}
        <VerifyEmailBanner />
        {user?.access_restricted ? <PaymentRestrictedGate /> : children}
      </main>
    </div>
  );
};

/** Full-page paywall shown instead of the app when the workspace owner's
 *  subscription has failed payment twice (see backend routes/stripe_billing.py
 *  _handle_invoice_payment_failed). Every route renders this until the owner
 *  fixes their card — nothing short of billing/auth/logout works while
 *  access_restricted is set (enforced server-side in shared/deps.py). */
const PaymentRestrictedGate = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const isOwner = user?.role === "owner";

  const openBillingPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the billing portal. Contact support@combinedwork.com.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-6 py-16" data-testid="payment-restricted-gate">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 grid place-items-center mx-auto mb-6">
          <Lock className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Workspace access restricted</h1>
        <p className="text-sm text-gray-500 mb-8">
          We tried twice to charge the card on file and both attempts failed. Update your payment
          method to restore access for your whole team immediately.
        </p>
        {isOwner ? (
          <Button
            onClick={openBillingPortal}
            disabled={busy}
            className="w-full bg-violet-600 hover:bg-violet-700"
            data-testid="restore-access-btn"
          >
            {busy ? "Opening billing portal…" : "Update payment method"}
          </Button>
        ) : (
          <p className="text-xs text-gray-400 mb-2">Ask your workspace owner to update the payment method.</p>
        )}
        <button
          onClick={handleLogout}
          className="mt-4 text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
        >
          Log out
        </button>
      </div>
    </div>
  );
};

const VerifyEmailBanner = () => {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only show for password-signup accounts that haven't verified yet.
  // Google-OAuth users already have a verified email by definition.
  if (!user) return null;
  if (user.email_verified) return null;
  if (user.auth_provider && user.auth_provider !== "email") return null;
  if (dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      toast.success("Verification email sent");
      if (refresh) refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send verification email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border-b border-amber-100 text-amber-700 px-4 md:px-8 lg:px-12 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
      data-testid="verify-email-banner"
    >
      <MailWarning className="w-4 h-4 shrink-0 text-amber-500" strokeWidth={1.7} />
      <span className="font-semibold text-amber-800">Verify your email</span>
      <span>to receive team invites, password resets, and PMO alerts.</span>
      <button
        onClick={resend}
        disabled={busy || sent}
        className="font-semibold text-violet-600 underline underline-offset-2 disabled:opacity-60"
        data-testid="resend-verification-btn"
      >
        {sent ? "Sent — check your inbox" : busy ? "Sending…" : "Resend email"}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-auto text-amber-400 hover:text-amber-600"
        title="Dismiss for this session"
        data-testid="dismiss-verify-banner"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default AppShell;
